import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Download, Calendar, Trash2, Clock, Plus, User, Target, Search, ChevronLeft, ChevronRight, Archive, Info, AlertTriangle } from 'lucide-react';
import { PageLayout } from '../../shared/PageLayout';
import { Card } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { Badge } from '../../../components/ui/Badge';
import { Input } from '../../../components/ui/Input';
import { Select } from '../../../components/ui/Select';
import { StudentData } from './StudentDetailPanel';
import { GroupId } from '../../../config/systemConfig';
import { error as logError } from '../../../utils/logger';
import { getAssessmentHistory, DOMAIN_META, type AssessmentHistoryEntry, type TeacherAssignment } from '../../../assessment';
import { TeacherV2Route } from '../TeacherSidebarV2';
import { DjayNarrator } from '../../../mascot';
import { CreateAssessmentPanel } from '../components/CreateAssessmentPanel';
import { ConfirmDialog } from '../../../components/shared/ConfirmDialog';
import { InsightBar } from '../components/InsightBar';
import { EmptyState } from '../../../components/shared/EmptyState';
import { DataFreshness } from '../../shared/DataFreshness';
import { ClassroomSwitcher } from '../components/ClassroomSwitcher';
import { dataLayer } from '../../../data/dataLayer';
import { formatRelativeTime } from '../../../utils/formatters';
import { downloadCsv } from '../../../utils/csvExport';
import type { Account, Classroom } from '../../../accounts';
import { containerVariants, springItemVariants as itemVariants, tableRowVariants } from '../shared/animationVariants';
interface AssessmentsPageProps {
  students: StudentData[];
  selectedGroup: GroupId;
  getModuleMastery: (learnerId: string, moduleId: string) => number;
  onNavigate?: (route: TeacherV2Route) => void;
  activeAccount?: Account;
  classrooms?: Classroom[];
  selectedClassroomId?: string | null;
  onSelectClassroom?: (id: string | null) => void;
  initialTab?: 'klaarzetten' | 'resultaten';
  initialSearchQuery?: string;
}
type MainTabType = 'klaarzetten' | 'resultaten';
const ITEMS_PER_PAGE = 20;
export function AssessmentsPage({
  students,
  selectedGroup,
  activeAccount,
  classrooms = [],
  selectedClassroomId,
  onSelectClassroom,
  initialTab,
  initialSearchQuery
}: AssessmentsPageProps) {
  const [assessmentHistory, setAssessmentHistory] = useState<AssessmentHistoryEntry[]>([]);
  const [assignments, setAssignments] = useState<TeacherAssignment[]>([]);
  const [isCreatePanelOpen, setIsCreatePanelOpen] = useState(false);
  const [mainTab, setMainTab] = useState<MainTabType>(initialTab || 'klaarzetten');
  const [searchQuery, setSearchQuery] = useState(initialSearchQuery || '');
  const [selectedDomain, setSelectedDomain] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [lastLoadedAt, setLastLoadedAt] = useState(() => new Date());
  /** AS-2026-07-P3-02: distinguish a failed load from "no data yet". */
  const [loadError, setLoadError] = useState(false);
  const [confirmAction, setConfirmAction] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    action: () => Promise<void>;
  }>({
    isOpen: false,
    title: '',
    message: '',
    action: async () => {}
  });
  // Apply initial values when they change
  useEffect(() => {
    if (initialTab) setMainTab(initialTab);
  }, [initialTab]);
  useEffect(() => {
    if (initialSearchQuery !== undefined) setSearchQuery(initialSearchQuery);
  }, [initialSearchQuery]);
  const loadData = useCallback(async () => {
    try {
      const [history, assignmentsData] = await Promise.all([getAssessmentHistory(selectedGroup), dataLayer.getTeacherAssignments()]);
      setAssessmentHistory(history);
      // Filter assignments for this classroom/group
      const allAssignments = Object.values(assignmentsData);
      const relevantAssignments = allAssignments.filter(a => (a.classroomId || null) === (selectedClassroomId || null) || !a.classroomId && a.group === selectedGroup);
      setAssignments(relevantAssignments.sort((a, b) => b.createdAt - a.createdAt));
      setLastLoadedAt(new Date());
      setLoadError(false);
    } catch (err) {
      logError('[AssessmentsPage] loadData failed:', err);
      setAssessmentHistory([]);
      setAssignments([]);
      setLoadError(true);
    }
  }, [selectedGroup, selectedClassroomId]);
  useEffect(() => {
    loadData();
  }, [loadData]);
  // Reset pagination when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, selectedDomain]);
  const filteredHistory = useMemo(() => {
    let filtered = assessmentHistory;
    // 1. Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(entry => {
        const student = students.find(s => s.id === entry.learnerId);
        const studentName = student ? student.name.toLowerCase() : 'onbekend';
        return studentName.includes(query);
      });
    }
    // 2. Domain / Type filter
    if (selectedDomain === 'niveaubepaling') {
      filtered = filtered.filter(entry => !entry.assignmentId);
    } else if (selectedDomain === 'toegewezen') {
      filtered = filtered.filter(entry => !!entry.assignmentId);
    } else if (selectedDomain !== 'all') {
      filtered = filtered.filter(entry => entry.domainId === selectedDomain);
    }
    return filtered;
  }, [assessmentHistory, searchQuery, selectedDomain, students]);
  const totalPages = Math.ceil(filteredHistory.length / ITEMS_PER_PAGE);
  const paginatedHistory = filteredHistory.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);
  const avgAssessmentScore = assessmentHistory.length > 0 ? Math.round(assessmentHistory.reduce((sum, a) => sum + a.score, 0) / assessmentHistory.length) : 0;
  const uniqueLearnersTested = useMemo(() => {
    const uniqueIds = new Set(assessmentHistory.map(a => a.learnerId));
    return uniqueIds.size;
  }, [assessmentHistory]);
  const handleExportAssessmentCsv = () => {
    const headers = ['Datum', 'Leerling', 'Type', 'Domein', 'Score (%)', 'Aantal Vragen', 'Tijd (min)'];
    const rows = filteredHistory.map(a => {
      const dateStr = new Date(a.completedAt).toLocaleDateString('nl-NL');
      const student = students.find(s => s.id === a.learnerId);
      const studentName = student ? student.name : 'Onbekend';
      const typeLabel = a.assignmentId ? 'Toegewezen toets' : 'Niveaubepaling';
      const domainLabel = a.domainId ? DOMAIN_META[a.domainId]?.label : 'Onbekend';
      return [dateStr, `"${studentName}"`, `"${typeLabel}"`, `"${domainLabel}"`, a.score, a.totalQuestions, Math.round(a.totalTimeSeconds / 60)].join(',');
    });
    const csvContent = [headers.join(','), ...rows].join('\n');
    downloadCsv(csvContent, 'toetsresultaten_export');
  };
  const handleDeleteAssignment = async (id: string) => {
    setConfirmAction({
      isOpen: true,
      title: 'Toets verwijderen',
      message: 'Weet je zeker dat je deze toets wilt verwijderen?',
      action: async () => {
        await dataLayer.deleteTeacherAssignment(id);
        loadData();
      }
    });
  };
  const handleArchiveAssignment = async (id: string) => {
    setConfirmAction({
      isOpen: true,
      title: 'Toets archiveren',
      message: 'Weet je zeker dat je deze toets wilt archiveren?',
      action: async () => {
        await dataLayer.archiveTeacherAssignment(id);
        loadData();
      }
    });
  };
  const domainOptions = [{
    value: 'all',
    label: 'Alle Toetsen'
  }, {
    value: 'niveaubepaling',
    label: 'Alleen Niveaubepalingen'
  }, {
    value: 'toegewezen',
    label: 'Alleen Toegewezen Toetsen'
  }, ...Object.entries(DOMAIN_META).map(([id, meta]) => ({
    value: id,
    label: `Domein: ${meta.label}`
  }))];
  return <PageLayout title="Toetsen" description="Toetsresultaten en niveaubepaling" data-id="element-9518" data-mp="13um7bg4ilvti">
      <motion.div className="max-w-5xl mx-auto space-y-6 pb-12" variants={containerVariants} initial="hidden" animate="visible" data-id="element-9519" data-mp="18rcne3gbkrex">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-[-12px]" data-id="element-9520" data-mp="g3gtf6i4xdlu">
          {classrooms.length > 0 && onSelectClassroom ? <ClassroomSwitcher classrooms={classrooms} selectedClassroomId={selectedClassroomId || null} onSelectClassroom={onSelectClassroom} activeTeacherId={activeAccount?.id} data-id="element-9521" data-mp="l21raz9wzl74" /> : <span data-id="element-9522" data-mp="21gjv9t1nytcu" />}
          <DataFreshness updatedAt={lastLoadedAt} onRefresh={loadData} data-id="element-9523" data-mp="21fryiewbypkz" />
        </div>

        {/* AS-2026-07-P3-02: a failed load must not read as "no toetsen yet". */}
        {loadError && <motion.div variants={itemVariants} role="alert" data-id="element-9524" data-mp="3h1c2dmduho2b">
            <Card className="p-4 border-amber-200 bg-amber-50 flex items-start gap-3" data-id="element-9525" data-mp="3c9156rahogdg">
              <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" data-id="element-9526" data-mp="37a7o6ovzu78j" />
              <div className="flex-1 min-w-0" data-id="element-9527" data-mp="378de65m9fzj8">
                <p className="text-sm font-bold text-amber-900" data-id="element-9528" data-mp="339ns7on5p9nz">
                  Toetsgegevens konden niet geladen worden
                </p>
                <p className="text-xs text-amber-800 mt-0.5" data-id="element-9529" data-mp="2ybmbvbh47og7">
                  Deze lijst is mogelijk leeg of incompleet. Er zijn geen
                  gegevens verwijderd — probeer te verversen.
                </p>
              </div>
              <button onClick={loadData} className="text-xs font-bold text-amber-900 underline hover:no-underline flex-shrink-0" data-id="element-9530" data-mp="rjlgc1utz1qg">
                Verversen
              </button>
            </Card>
          </motion.div>}

        {/* Page-level Tab Switcher */}
        <motion.div variants={itemVariants} className="flex justify-center sm:justify-start" data-id="element-9531" data-mp="1j9ptfupthd2u">
          <div className="bg-slate-100 p-1 rounded-xl inline-flex relative" data-id="element-9532" data-mp="1y7i8klrgbzgi">
            <button onClick={() => setMainTab('klaarzetten')} className={`relative px-6 py-2.5 rounded-lg text-sm font-bold transition-colors z-10 ${mainTab === 'klaarzetten' ? 'text-slate-900' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-9533" data-mp="1t9yublur7mok">
              {mainTab === 'klaarzetten' && <motion.div layoutId="activeTabIndicator" className="absolute inset-0 bg-white rounded-lg shadow-sm" initial={false} transition={{
              type: 'spring',
              stiffness: 500,
              damping: 30
            }} data-id="element-9534" data-mp="1fah1mvec7p9x" />}
              <span className="relative z-10" data-id="element-9535" data-mp="2tgc2317ul1gf">Toetsen</span>
            </button>
            <button onClick={() => setMainTab('resultaten')} className={`relative px-6 py-2.5 rounded-lg text-sm font-bold transition-colors z-10 ${mainTab === 'resultaten' ? 'text-slate-900' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-9536" data-mp="2y83s9an3d561">
              {mainTab === 'resultaten' && <motion.div layoutId="activeTabIndicator" className="absolute inset-0 bg-white rounded-lg shadow-sm" initial={false} transition={{
              type: 'spring',
              stiffness: 500,
              damping: 30
            }} data-id="element-9537" data-mp="3c84uatf3ein3" />}
              <span className="relative z-10" data-id="element-9538" data-mp="1rqp20yb7nesy">Resultaten</span>
            </button>
          </div>
        </motion.div>

        {mainTab === 'klaarzetten' && <motion.div key="klaarzetten-tab" initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -10
      }} className="space-y-6" data-id="element-9539" data-mp="2ams4f0t48on">
            {/* Toets klaarzetten CTA */}
            <Card className="p-5 border-l-4 border-l-orange-400 border-slate-200 shadow-sm bg-white flex flex-col sm:flex-row items-start gap-4" data-id="element-9540" data-mp="3fpj3xmlbgc0h">
              <DjayNarrator size="md" emotion="pointing" data-id="element-9541" data-mp="3apeolzw59nbu" />
              <div className="flex-1" data-id="element-9542" data-mp="35rd02zfy8vsz">
                <h2 className="text-lg font-black text-slate-900 mb-1" data-id="element-9543" data-mp="35sehrmrh0gjo">
                  Toets klaarzetten
                </h2>
                <p className="text-sm text-slate-600 mb-3 max-w-xl leading-relaxed" data-id="element-9544" data-mp="30u37xc16e61g">
                  Zet een toets klaar voor je klas of specifieke leerlingen.
                </p>
                <Button onClick={() => setIsCreatePanelOpen(true)} className="bg-orange-500 hover:bg-orange-600 text-white shadow-sm" size="sm" data-id="element-9545" data-mp="z1ez8n5riqea">
                  <Plus className="w-4 h-4 mr-2" data-id="element-9546" data-mp="1bhen4zafdcmn" />
                  Nieuwe toets klaarzetten
                </Button>
              </div>
            </Card>

            {/* Active Assignments Section */}
            {assignments.length > 0 ? <div className="space-y-4" data-id="element-9547" data-mp="ojjpy8webbk0">
                <h2 className="text-xl font-black text-slate-900" data-id="element-9548" data-mp="okuvoe9eitdr">
                  Klaargezette Toetsen
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-9549" data-mp="smgd8cwvrvua">
                  {assignments.filter(a => a.status !== 'archived').map(assignment => {
              const meta = DOMAIN_META[assignment.domainId as keyof typeof DOMAIN_META];
              /*
               AS-2026-07-P1-04 (July 2026): progress is read from the
               assignment LEDGER, intersected with the learners this
               assignment actually targets.
                The previous implementation counted any attempt in the
               same domain finished after `createdAt`. That credited
               non-targeted classmates, credited self-initiated
               niveaubepalingen in the same domain, double-counted a
               learner who tested twice, and then hid the overcount
               behind Math.min() — which could silently flip the card
               to "voltooid" while nobody targeted had finished.
                `completedByStudentIds` is the authoritative signal
               (see TeacherAssignment docs); it is written only on a
               durably-persisted completion.
               */
              const targetIds = assignment.wholeClass ? students.map(s => s.id) : assignment.targetStudentIds;
              const targetIdSet = new Set(targetIds);
              const targetCount = targetIdSet.size;
              const completedCount = (assignment.completedByStudentIds || []).filter(sid => targetIdSet.has(sid)).length;
              const progressPercent = targetCount > 0 ? Math.round(completedCount / targetCount * 100) : 0;
              let status = assignment.status;
              if (assignment.deadline && Date.now() > assignment.deadline) status = 'expired';
              if (completedCount >= targetCount && targetCount > 0) status = 'completed';
              const Icon = meta?.icon;
              return <Card key={assignment.id} className="p-4 border-slate-200 flex flex-col bg-white shadow-sm hover:shadow-md transition-shadow" data-id="element-9550" data-mp="2c2b37zcmn2e0">
                          <div className="flex items-start justify-between mb-3" data-id="element-9551" data-mp="1xc3uibcf8xqi">
                            <div className="flex items-center gap-3" data-id="element-9552" data-mp="2uzc8lla3f4ih">
                              <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${meta?.bgColor || 'bg-slate-100'} ${meta?.color || 'text-slate-500'}`} data-id="element-9553" data-mp="2uyknj4e4gp6g">
                                {Icon ? <Icon className="w-4 h-4" data-id="element-9554" data-mp="2h0488p271qcp" /> : '📝'}
                              </div>
                              <div data-id="element-9555" data-mp="19miz2sz3080c">
                                <div className="flex items-center gap-2" data-id="element-9556" data-mp="19nkmlwxaamyy">
                                  <h3 className="font-bold text-sm text-slate-900" data-id="element-9557" data-mp="15k61wsyehif6">
                                    {assignment.domainLabel}
                                  </h3>
                                  {assignment.type === 'general' && <Badge variant="default" className="bg-blue-50 text-blue-700 border-blue-200 text-[10px] py-0 px-1.5" data-id="element-9558" data-mp="3l707fxd9q968">
                                      Algemeen
                                    </Badge>}
                                  {assignment.type === 'targeted' && <Badge variant="default" className="bg-purple-50 text-purple-700 border-purple-200 text-[10px] py-0 px-1.5" data-id="element-9559" data-mp="cum76oockcs6">
                                      Gericht
                                    </Badge>}
                                </div>
                                {assignment.type === 'targeted' && assignment.selectedSkillSummary && <div className="text-[10px] text-slate-500 mt-0.5 line-clamp-1" data-id="element-9560" data-mp="z1p02dp27y53">
                                      {assignment.selectedSkillSummary}
                                    </div>}
                                <div className="text-[10px] text-slate-500 flex items-center gap-1.5 mt-1" data-id="element-9561" data-mp="6dt5pfmyqibh">
                                  <Calendar className="w-3 h-3" data-id="element-9562" data-mp="2diof6tq05hb" />
                                  {new Date(assignment.createdAt).toLocaleDateString('nl-NL')}
                                  {assignment.deadline && <>
                                      <span data-id="element-9563" data-mp="1mrj88hyfoyj0">•</span>
                                      <span className="text-orange-600" data-id="element-9564" data-mp="1mr16j5t05s3p">
                                        Deadline:{' '}
                                        {new Date(assignment.deadline).toLocaleDateString('nl-NL')}
                                      </span>
                                    </>}
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-1" data-id="element-9565" data-mp="3ofj3lweztnq3">
                              <button onClick={() => handleArchiveAssignment(assignment.id)} className="p-1.5 text-slate-400 hover:text-amber-500 hover:bg-amber-50 rounded-md transition-colors" aria-label="Archiveren" title="Archiveren" data-id="element-9566" data-mp="3sdyvadm0rk9u">
                                <Archive className="w-4 h-4" data-id="element-9567" data-mp="w6kpocrmoaf3" />
                              </button>
                              <button onClick={() => handleDeleteAssignment(assignment.id)} className="p-1.5 text-slate-400 hover:text-rose-500 hover:bg-rose-50 rounded-md transition-colors" aria-label="Verwijderen" data-id="element-9568" data-mp="116758suxi2r2">
                                <Trash2 className="w-4 h-4" data-id="element-9569" data-mp="ig8vm8bhg1lb" />
                              </button>
                            </div>
                          </div>

                          <div className="mt-auto space-y-2" data-id="element-9570" data-mp="8h02tiscst0j">
                            <div className="flex items-center justify-between text-xs" data-id="element-9571" data-mp="8jcjlsmdkxc0">
                              <div className="flex items-center gap-2 flex-wrap" data-id="element-9572" data-mp="1yuh5p3f2gig2">
                                <span className="text-slate-600 font-medium" data-id="element-9573" data-mp="22tzsk4qg73lj">
                                  {completedCount} van {targetCount} voltooid
                                </span>
                                {/*
                                 F-AS-R1-ASSIGNED-ASSESSMENT-EXIT: surface
                                 students who started and abandoned this
                                 assignment so the teacher can follow up.
                                 Hidden when zero — keeps the card calm.
                                 Filters out students who later completed.
                                 */}
                                {(() => {
                        const abandoned = assignment.abandonedByStudentIds || [];
                        const completedSet = new Set(assignment.completedByStudentIds || []);
                        const stillAbandoned = abandoned.filter(sid => !completedSet.has(sid));
                        if (stillAbandoned.length === 0) return null;
                        return <span className="inline-flex items-center gap-1 text-amber-700 font-medium" title={`${stillAbandoned.length} leerling${stillAbandoned.length === 1 ? '' : 'en'} is gestopt voor het einde — mogelijk een gesprek waard.`} data-id="element-9574" data-mp="uiv4qsvlhlp5">
                                      <AlertTriangle className="w-3 h-3" data-id="element-9575" data-mp="1dao0gv21tl0k" />
                                      {stillAbandoned.length} gestopt
                                    </span>;
                      })()}
                              </div>
                              {status === 'active' && <Badge variant="default" className="bg-indigo-100 text-indigo-700 text-[10px] py-0 px-1.5" data-id="element-9576" data-mp="1raqbbxbxve6b">
                                  Loopt nu
                                </Badge>}
                              {status === 'completed' && <Badge variant="success" className="bg-emerald-100 text-emerald-700 text-[10px] py-0 px-1.5" data-id="element-9577" data-mp="2xu18kq1b33fj">
                                  Afgerond
                                </Badge>}
                              {status === 'expired' && <Badge variant="danger" className="bg-rose-100 text-rose-700 text-[10px] py-0 px-1.5" data-id="element-9578" data-mp="3gqsw5bnvs4pe">
                                  Deadline voorbij
                                </Badge>}
                            </div>
                            <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden" data-id="element-9579" data-mp="1v8s9n7odqjt">
                              <div className={`h-full rounded-full transition-all duration-1000 ${status === 'completed' ? 'bg-emerald-500' : status === 'expired' ? 'bg-rose-500' : 'bg-indigo-500'}`} style={{
                      width: `${progressPercent}%`
                    }} data-id="element-9580" data-mp="3k0u819l1wjbo" />
                            </div>
                          </div>
                        </Card>;
            })}
                </div>
              </div> : <motion.div className="mb-6" initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.4
        }} data-id="element-9581" data-mp="zhoe1174xad6">
                <EmptyState title="Geen actieve toetsen" message="Je hebt op dit moment geen toetsen klaargezet voor je leerlingen." emotion="happy" colorScheme="slate" data-id="element-9582" data-mp="1nd9qsowll5nm" />
              </motion.div>}
          </motion.div>}

        {mainTab === 'resultaten' && <motion.div key="resultaten-tab" initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -10
      }} className="space-y-6" data-id="element-9583" data-mp="qp3jfu8ajz5n">
            {/* Insight Bar */}
            <InsightBar message={<>
                  <p data-id="element-9585" data-mp="2j2i94l694txv">
                    {assessmentHistory.length === 0 ? 'Er zijn nog geen toetsen afgenomen. Zet een toets klaar om te beginnen!' : `Er zijn ${assessmentHistory.length} toetsen afgenomen bij ${uniqueLearnersTested} leerlingen. De gemiddelde score is ${avgAssessmentScore}%.`}
                  </p>
                  <p className="text-xs text-indigo-500/70 mt-1.5 flex items-center gap-1" data-id="element-9586" data-mp="1zfkmoghps5s4">
                    <Info className="w-3 h-3" data-id="element-9587" data-mp="1zdq1r26g0do1" />
                    Indicatief — geen officiële Cito-toets.
                  </p>
                </>} data-id="element-9584" data-mp="2b2ccc7ucrine" />

            {/* Toetsresultaten Section */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4" data-id="element-9588" data-mp="2e4720i7ccxjc">
              <div data-id="element-9589" data-mp="1gpcpef0p49ju">
                <h2 className="text-xl font-black text-slate-900 mb-1" data-id="element-9590" data-mp="1go1jo9nowrq3">
                  Toetsresultaten Historie
                </h2>
                <p className="text-sm text-slate-500" data-id="element-9591" data-mp="1lmbc4ci15e80">
                  Overzicht van alle gemaakte toetsen in deze groep.
                </p>
              </div>
              <Button onClick={handleExportAssessmentCsv} variant="outline" disabled={filteredHistory.length === 0} size="sm" className="border-slate-200 text-slate-600 hover:bg-slate-50" data-id="element-9592" data-mp="196lhbwtplf9k">
                <Download className="w-4 h-4 mr-2" data-id="element-9593" data-mp="v6amyjt6xijn" />
                Exporteer CSV
              </Button>
            </div>

            {/* Filter Bar */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 mb-6" data-id="element-9594" data-mp="qczfqhkguzr7">
              <div className="w-full sm:w-1/2 md:w-1/3" data-id="element-9595" data-mp="ldv8vxl0le41">
                <Input placeholder="Zoek leerling..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} icon={<Search className="w-4 h-4" data-id="element-9597" data-mp="7fojz6y1mogi" />} size="sm" data-id="element-9596" data-mp="ghxrrp1xg3q7" />
              </div>
              <div className="w-full sm:w-1/2 md:w-1/3" data-id="element-9598" data-mp="1vdxpd6h7s772">
                <Select options={domainOptions} value={selectedDomain} onChange={e => setSelectedDomain(e.target.value)} size="sm" data-id="element-9599" data-mp="1vb1nj728wydc" />
              </div>
            </div>

            {paginatedHistory.length > 0 ? <>
                <motion.div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6" variants={containerVariants} initial="hidden" animate="visible" data-id="element-9600" data-mp="1clv3ngppu0py">
                  {paginatedHistory.map((entry, idx) => {
              const dateStr = new Date(entry.completedAt).toLocaleDateString('nl-NL', {
                day: 'numeric',
                month: 'short',
                year: 'numeric'
              });
              const relativeDate = formatRelativeTime(entry.completedAt);
              const isHigh = entry.score >= 70;
              const isMedium = entry.score >= 50 && entry.score < 70;
              const domainMeta = entry.domainId ? DOMAIN_META[entry.domainId] : null;
              const student = students.find(s => s.id === entry.learnerId);
              const studentName = student ? student.name : 'Onbekend';
              return <motion.div key={entry.attemptId} variants={tableRowVariants} className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow flex flex-col" data-id="element-9601" data-mp="t0cujxjasgoo" data-mpi={idx}>
                        <div className="flex justify-between items-start mb-3" data-id="element-9602" data-mp="1gqgyk60eeig7">
                          <div className="flex items-center gap-3" data-id="element-9603" data-mp="1bsnqt8jg5gdt">
                            <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center text-slate-500 flex-shrink-0 border border-slate-200" data-id="element-9604" data-mp="148gz1y5lvloh">
                              <User className="w-5 h-5" data-id="element-9605" data-mp="v7irx6zvey1o" />
                            </div>
                            <div data-id="element-9606" data-mp="v9c0iru1nscb">
                              <div className="font-bold text-slate-900" data-id="element-9607" data-mp="q8xuffc06sgx">
                                {studentName}
                              </div>
                              <div className="text-xs text-slate-500 flex items-center gap-1" data-id="element-9608" data-mp="lbfswa0bffiy">
                                <Clock className="w-3 h-3" data-id="element-9609" data-mp="ggi865tobhq8" />
                                {relativeDate}{' '}
                                <span className="text-slate-300" data-id="element-9610" data-mp="cen6v1bqt4cg">•</span>{' '}
                                {dateStr}
                              </div>
                            </div>
                          </div>
                          <div className="text-right" data-id="element-9611" data-mp="3tpeohajdkyvx">
                            <div className={`text-2xl font-black ${isHigh ? 'text-emerald-600' : isMedium ? 'text-amber-600' : 'text-rose-600'}`} data-id="element-9612" data-mp="3tpwq6mot45b8">
                              {entry.score}%
                            </div>
                          </div>
                        </div>

                        <div className="mt-auto pt-3 border-t border-slate-100 flex items-center justify-between" data-id="element-9613" data-mp="2dmbxrembqhz">
                          <div className="flex items-center gap-2" data-id="element-9614" data-mp="30vhv8t943uua">
                            {domainMeta ? <div className={`flex items-center gap-1.5 text-xs font-bold px-2 py-1 rounded-md ${domainMeta.bgColor} ${domainMeta.color}`} data-id="element-9615" data-mp="35ukzibh9px17">
                                <domainMeta.icon className="w-3.5 h-3.5" data-id="element-9616" data-mp="3fkxjo2l67o6t" />
                                {domainMeta.label}
                              </div> : <div className="flex items-center gap-1.5 text-xs font-bold px-2 py-1 rounded-md bg-slate-100 text-slate-600" data-id="element-9617" data-mp="2n1h3ijgkv83f">
                                <Target className="w-3.5 h-3.5" data-id="element-9618" data-mp="2a3sqduoc0fax" />
                                Algemeen
                              </div>}

                            {entry.assignmentId ? <span className="text-[10px] font-bold text-orange-600 uppercase tracking-wider" data-id="element-9619" data-mp="2judntes9rds9">
                                Toegewezen
                              </span> : <span className="text-[10px] font-bold text-blue-600 uppercase tracking-wider" data-id="element-9620" data-mp="1r6850xkrzyv2">
                                Niveaubepaling
                              </span>}
                          </div>

                          <div className="text-xs text-slate-500 font-medium" data-id="element-9621" data-mp="3hgl25gedkkht">
                            {entry.totalQuestions} vr{' '}
                            <span className="text-slate-300 mx-1" data-id="element-9622" data-mp="3chhvjriqy67q">•</span>{' '}
                            {Math.round(entry.totalTimeSeconds / 60)} min
                          </div>
                        </div>
                      </motion.div>;
            })}
                </motion.div>

                {/* Pagination Controls */}
                {totalPages > 1 && <div className="flex items-center justify-between border-t border-slate-100 pt-4 mb-6" data-id="element-9623" data-mp="3f2y0ox9ifkz5">
                    <div className="text-sm text-slate-500" data-id="element-9624" data-mp="6nec4bjsaqqf">
                      Pagina{' '}
                      <span className="font-bold text-slate-900" data-id="element-9625" data-mp="bl7e0se20ykw">
                        {currentPage}
                      </span>{' '}
                      van{' '}
                      <span className="font-bold text-slate-900" data-id="element-9626" data-mp="1v4iakfjt6iv">
                        {totalPages}
                      </span>
                    </div>
                    <div className="flex items-center gap-2" data-id="element-9627" data-mp="pjnlwmnnkv4m">
                      <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className="px-2" data-id="element-9628" data-mp="udrutbghbd8c">
                        <ChevronLeft className="w-4 h-4" data-id="element-9629" data-mp="2ou0abmqpbmk2" />
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className="px-2" data-id="element-9630" data-mp="3ljhoyr7nwixw">
                        <ChevronRight className="w-4 h-4" data-id="element-9631" data-mp="37q9wkxbrbjf5" />
                      </Button>
                    </div>
                  </div>}
              </> : <motion.div className="mb-6" initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.4
        }} data-id="element-9632" data-mp="3cuzb3p0b9pw">
                <EmptyState title="Geen resultaten gevonden" message="Er zijn geen toetsen die voldoen aan je huidige filters." emotion="happy" colorScheme="slate" data-id="element-9633" data-mp="201qdb48hmuvk" />
              </motion.div>}
          </motion.div>}
      </motion.div>

      <CreateAssessmentPanel open={isCreatePanelOpen} onClose={() => setIsCreatePanelOpen(false)} students={students} selectedGroup={selectedGroup} teacherId={activeAccount?.id || ''} classroomId={selectedClassroomId || null} onSuccess={loadData} data-id="element-9634" data-mp="2erxocyw61bm8" />

      <ConfirmDialog open={confirmAction.isOpen} title={confirmAction.title} message={confirmAction.message} onConfirm={async () => {
      await confirmAction.action();
      setConfirmAction(prev => ({
        ...prev,
        isOpen: false
      }));
    }} onCancel={() => setConfirmAction(prev => ({
      ...prev,
      isOpen: false
    }))} data-id="element-9635" data-mp="sn398vi9692n" />
    </PageLayout>;
}